
import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Package,
  Loader2,
  Filter,
  Minus,
  Plus,
  ChevronLeft,
  ChevronRight,
  User,
  ChevronDown,
  ChevronUp,
  FileText,
  ShoppingCart,
  AlertCircle,
} from "lucide-react";
import { materialRequestService } from "../services/materialRequestService";

import { MaterialRequestHeader } from "./MaterialRequestHeader";
import { MaterialSearchField } from "./MaterialSearchField";
import { MaterialRequestFooter } from "./MaterialRequestFooter";
import { NormalSearch } from "../../client_contact_stages/Initial-Quotation/components/NormalSearch";
import { MATERIAL_CATEGORIES } from "../constants/materialRequestConstants";
// import { useMaterialInventory } from "../hooks/useMaterialInventory";
import { useProjectTeamMembers } from "../hooks/useProjectTeamMembers";
import api from "@/services/api";
import { HardwareSelectionTable } from "../../client_contact_stages/Initial-Quotation/components/HardwareSelectionTable";
import { PurchasesTable } from "../../verve-inventory/components/PurchasesTable";
import { orderingQuotationService } from "../../ordering/services/orderingQuotationService";

const officerDisplayName = (o) =>
  o?.name ||
  `${o?.firstName || ""} ${o?.lastName || ""}`.trim() ||
  o?.email ||
  "Member";

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

export const RequestMaterialsModal = ({
  isOpen,
  onClose,
  project: initialProject,
  projects = [],
  onSuccess,
  initialItems = EMPTY_OBJECT,
  initialItemsData = EMPTY_ARRAY,
  initialDepartment = "",
  viewerRole = 'installation_team',
  requestId = null,
  source = "overview",
}) => {
  const [activeTab, setActiveTab] = useState("tab2");
  const [selectedProject, setSelectedProject] = useState(initialProject);

  console.log("Project passed to modal:", initialProject);
  console.log("Selected project:", selectedProject);

  useEffect(() => {
    setSelectedProject(initialProject);
  }, [initialProject]);

  // Fetch full project details (with fieldOfficers) when selectedProject lacks them
  useEffect(() => {
    const fetchProjectWithTeam = async () => {
      if (!selectedProject?._id) return;
      // Check if fieldOfficers are already populated objects
      const officers = selectedProject.fieldOfficers;
      const hasPopulatedOfficers = Array.isArray(officers) && officers.length > 0 && typeof officers[0] === 'object' && officers[0]?._id;
      if (hasPopulatedOfficers) return;
      
      try {
        let res;
        try {
          res = await api.get(`/verve-projects/${selectedProject._id}`);
        } catch (e) {
          res = await api.get(`/projects/${selectedProject._id}`);
        }
        const fullProject = res.data?.data || res.data;
        if (fullProject?.fieldOfficers) {
          setSelectedProject(prev => ({
            ...prev,
            ...fullProject,
            // Preserve any existing props that the caller set
            _id: prev._id,
          }));
        }
      } catch (err) {
        console.error("Failed to fetch project team members for roles:", err);
      }
    };
    fetchProjectWithTeam();
  }, [selectedProject?._id]);

  const [purchaseMasterItems, setPurchaseMasterItems] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchPurchases = async () => {
      if (!isOpen) return;
      try {
        setLoadingPurchases(true);
        const res = await api.get('/v1/purchase-master');
        const data = res.data?.data || res.data || [];
     const mapped = data.map(item => ({
  _id: item._id,
  name: item.itemName || item.description || "Unnamed Material",
  category: item.category?.categoryName || item.category || "",
  stock: item.stock ?? item.availableQty ?? item.quantity ?? 0,
  amount: item.unitPrice || item.purchaseRate || item.blumPrice || item.vervePrice || 0,
  itemCode: item.itemCode || item.item?.itemCode || "N/A",
  vendorName: item.vendor?.vendorName || item.vendor?.name || item.vendorName || "Unknown",
  queued: item.queued || 0,
  rawData: item
}));
        setPurchaseMasterItems(mapped);
      } catch (err) {
        console.error("Failed to fetch purchase-master", err);
      } finally {
        setLoadingPurchases(false);
      }
    };
    fetchPurchases();
  }, [isOpen]);

  const { members: allMembers } = useProjectTeamMembers(selectedProject);

  const [selectedItems, setSelectedItems] = useState(initialItems);

  const filteredPurchaseItems = useMemo(() => {
    const inStockItems = purchaseMasterItems.filter(item => {
      const itemRaw = item.rawData || {};
      const itemIds = [
        item._id,
        itemRaw._id,
        itemRaw.itemId,
        typeof itemRaw.inventoryId === 'object' ? (itemRaw.inventoryId?._id || itemRaw.inventoryId?.id) : itemRaw.inventoryId,
        typeof itemRaw.item === 'object' ? (itemRaw.item?._id || itemRaw.item?.id) : itemRaw.item
      ].filter(id => id && id !== 'undefined' && id !== 'null').map(String);
      
      const hasInitial = initialItems && itemIds.some(id => initialItems[id] > 0);
      const hasSelected = selectedItems && selectedItems[item._id] > 0;
      
      return item.stock > 0 || hasInitial || hasSelected;
    });
    if (!searchTerm) return inStockItems;
    
    const term = searchTerm.toLowerCase();
    return inStockItems.filter(item => 
      item.name.toLowerCase().includes(term) || 
      item.category.toLowerCase().includes(term)
    );
  }, [purchaseMasterItems, searchTerm, initialItems, selectedItems]);

  const selectedPurchaseItems = useMemo(() => {
    return purchaseMasterItems
      .filter((item) => selectedItems[item._id] > 0)
      .map((item) => ({
        ...item,
        rawData: {
          ...item.rawData,
          quantity: selectedItems[item._id]
        }
      }));
  }, [purchaseMasterItems, selectedItems]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDepartment, setSelectedDepartment] =
    useState(initialDepartment);
  const [selectedOfficerId, setSelectedOfficerId] = useState("");
  const [assignmentError, setAssignmentError] = useState("");
  const [formData, setFormData] = useState({
    expectedDate: "",
    serviceType: "Verve",
    notes: "",
  });
  const [successMessage, setSuccessMessage] = useState("");

  const [initialQuotationItems, setInitialQuotationItems] = useState([]);
  const [initialQuotationEstimatedPrice, setInitialQuotationEstimatedPrice] = useState(0);
  const [loadingQuotation, setLoadingQuotation] = useState(false);
  const [orderingItems, setOrderingItems] = useState([]);
  const [loadingOrdering, setLoadingOrdering] = useState(false);
  const [openFinalQuotation, setOpenFinalQuotation] = useState(true);
  const [openOrdering, setOpenOrdering] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [hardwareItems, setHardwareItems] = useState([]);
  const [hasPrefilled, setHasPrefilled] = useState(false);
  const [hasMappedInitial, setHasMappedInitial] = useState(false);

  useEffect(() => {
    if (isOpen && purchaseMasterItems.length > 0 && !hasMappedInitial) {
      if (initialItems && Object.keys(initialItems).length > 0) {
        const remapped = { ...selectedItems };
        let changed = false;
        
        Object.entries(initialItems).forEach(([key, qty]) => {
          const qi = initialItemsData.find((it) => {
            const itemField = it.item || (it.inventoryId && it.inventoryId.item);
            const itemId = itemField?._id || itemField?.id || it.itemId;
            const invId = it.inventoryId?._id || it.inventoryId?.id || it.inventoryId;
            const id = (typeof itemId === "string" ? itemId : null) || (typeof invId === "string" ? invId : null) || it._id;
            return String(id) === String(key);
          });

          const matchedItem = purchaseMasterItems.find(item => {
            const itemRaw = item.rawData || {};
            const itemIds = [
              item._id,
              itemRaw._id,
              itemRaw.itemId,
              typeof itemRaw.inventoryId === 'object' ? (itemRaw.inventoryId?._id || itemRaw.inventoryId?.id) : itemRaw.inventoryId,
              typeof itemRaw.item === 'object' ? (itemRaw.item?._id || itemRaw.item?.id) : itemRaw.item
            ].filter(id => id && id !== 'undefined' && id !== 'null').map(String);
            
            if (itemIds.includes(String(key))) return true;

            if (qi) {
              const qiRaw = qi.rawData || qi || {};
              const qiInvObj = typeof qiRaw.inventoryId === 'object' ? qiRaw.inventoryId : {};
              const qiInvItemObj = typeof qiInvObj?.item === 'object' ? qiInvObj.item : {};

              const qNames = [
                qi.name,
                qi.itemName,
                qiRaw.itemName,
                qiRaw.description,
                qiRaw.item?.itemName,
                qiInvObj?.itemName,
                qiInvObj?.description,
                qiInvItemObj?.itemName,
                qiInvItemObj?.description
              ].filter(Boolean).map(s => String(s).toLowerCase().trim());

              const iNames = [
                item.name,
                itemRaw.itemName,
                itemRaw.description,
                itemRaw.item?.itemName
              ].filter(Boolean).map(s => String(s).toLowerCase().trim());

              const isNameMatch = qNames.some(qName => iNames.includes(qName));

              const qiItemCode = qiRaw.itemCode || qiRaw.item?.itemCode || qiRaw.articleId || qiInvObj?.itemCode || qiInvObj?.articleId || qiInvItemObj?.itemCode || qiInvItemObj?.articleId;
              const itemItemCode = item.itemCode || itemRaw.itemCode || itemRaw.item?.itemCode || itemRaw.articleId;
              
              const isCodeMatch = qiItemCode && itemItemCode && String(qiItemCode).toLowerCase().trim() !== "n/a" && String(qiItemCode).toLowerCase().trim() === String(itemItemCode).toLowerCase().trim();

              return isNameMatch || isCodeMatch;
            }
            return false;
          });
          
          if (matchedItem && String(matchedItem._id) !== String(key)) {
            remapped[matchedItem._id] = qty;
            delete remapped[key];
            changed = true;
          }
        });
        
        if (changed) {
          setSelectedItems(remapped);
        }
      }
      setHasMappedInitial(true);
    }
  }, [isOpen, purchaseMasterItems, initialItems, initialItemsData, hasMappedInitial, selectedItems]);

  const sortedFilteredPurchaseItems = useMemo(() => {
    const enriched = filteredPurchaseItems.map((item) => {
      const isMatch = (qi) => {
        const qiRaw = qi.rawData || {};
        const itemRaw = item.rawData || {};

        const qiInvObj = typeof qiRaw.inventoryId === 'object' ? qiRaw.inventoryId : {};
        const qiInvItemObj = typeof qiInvObj?.item === 'object' ? qiInvObj.item : {};

        const qiIds = [
          qi._id,
          qiRaw._id,
          qiRaw.itemId,
          qiInvObj?._id,
          qiInvObj?.id,
          qiInvItemObj?._id,
          qiInvItemObj?.id,
          typeof qiRaw.inventoryId === 'string' ? qiRaw.inventoryId : null,
          typeof qiRaw.item === 'string' ? qiRaw.item : null
        ].filter(Boolean).map(String);

        const itemIds = [
          item._id,
          itemRaw._id,
          itemRaw.itemId,
          typeof itemRaw.inventoryId === 'object' ? (itemRaw.inventoryId?._id || itemRaw.inventoryId?.id) : itemRaw.inventoryId,
          typeof itemRaw.item === 'object' ? (itemRaw.item?._id || itemRaw.item?.id) : itemRaw.item
        ].filter(id => id && id !== 'undefined' && id !== 'null').map(String);

        const isIdMatch = qiIds.some(qId => itemIds.includes(qId));

        const qNames = [
          qi.name,
          qiRaw.itemName,
          qiRaw.description,
          qiRaw.item?.itemName,
          qiInvObj?.itemName,
          qiInvObj?.description,
          qiInvItemObj?.itemName,
          qiInvItemObj?.description
        ].filter(Boolean).map(s => String(s).toLowerCase().trim());

        const iNames = [
          item.name,
          itemRaw.itemName,
          itemRaw.description,
          itemRaw.item?.itemName
        ].filter(Boolean).map(s => String(s).toLowerCase().trim());

        const isNameMatch = qNames.some(qName => iNames.includes(qName));

        const qiItemCode = qiRaw.itemCode || qiRaw.item?.itemCode || qiRaw.articleId || qiInvObj?.itemCode || qiInvObj?.articleId || qiInvItemObj?.itemCode || qiInvItemObj?.articleId;
        const itemItemCode = item.itemCode || itemRaw.itemCode || itemRaw.item?.itemCode || itemRaw.articleId;
        
        const isCodeMatch = qiItemCode && itemItemCode && String(qiItemCode).toLowerCase().trim() !== "n/a" && String(qiItemCode).toLowerCase().trim() === String(itemItemCode).toLowerCase().trim();

        return isIdMatch || isNameMatch || isCodeMatch;
      };

      const matchedQi = initialQuotationItems.find(isMatch);
      const matchedHardware = hardwareItems.find(isMatch);

      const isRequestedInInitialQuotation = !!matchedQi;
      const itemRaw = item.rawData || {};
      const itemIds = [
        item._id,
        itemRaw._id,
        itemRaw.itemId,
        typeof itemRaw.inventoryId === 'object' ? (itemRaw.inventoryId?._id || itemRaw.inventoryId?.id) : itemRaw.inventoryId,
        typeof itemRaw.item === 'object' ? (itemRaw.item?._id || itemRaw.item?.id) : itemRaw.item
      ].filter(id => id && id !== 'undefined' && id !== 'null').map(String);
      const isInitiallySelected = !!(initialItems && itemIds.some(id => initialItems[id] > 0));
      const isPreviouslyRequested = isRequestedInInitialQuotation || !!matchedHardware || isInitiallySelected;
      const quotationQuantity = matchedQi ? Number(matchedQi.rawData?.quantity || matchedQi.quantity || 1) : 0;
      const quotationPrice = matchedQi ? Number(matchedQi.amount || matchedQi.vervePrice || matchedQi.unitPrice || matchedQi.netAmount || 0) : 0;

      return {
        item,
        isRequestedInInitialQuotation,
        isPreviouslyRequested,
        quotationQuantity,
        quotationPrice
      };
    });

    enriched.sort((a, b) => {
      if (a.isPreviouslyRequested && !b.isPreviouslyRequested) return -1;
      if (!a.isPreviouslyRequested && b.isPreviouslyRequested) return 1;
      return 0;
    });

    return enriched;
  }, [filteredPurchaseItems, initialQuotationItems, hardwareItems, initialItems]);
 
  useEffect(() => {
    if (isOpen) {
      setHasPrefilled(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || hasPrefilled || sortedFilteredPurchaseItems.length === 0) return;
    
    let shouldUpdate = false;
    const newSelectedItems = { ...selectedItems };
    
    sortedFilteredPurchaseItems.forEach(({ item, isRequestedInInitialQuotation, quotationQuantity }) => {
      if (isRequestedInInitialQuotation && newSelectedItems[item._id] === undefined && quotationQuantity > 0) {
        newSelectedItems[item._id] = quotationQuantity;
        shouldUpdate = true;
      }
    });

    if (shouldUpdate) {
      setSelectedItems(newSelectedItems);
      setHasPrefilled(true);
    } else if (sortedFilteredPurchaseItems.some(x => x.isRequestedInInitialQuotation)) {
      setHasPrefilled(true);
    } else if (initialQuotationItems.length > 0 && purchaseMasterItems.length > 0) {
      setHasPrefilled(true);
    }
  }, [isOpen, hasPrefilled, sortedFilteredPurchaseItems, selectedItems, initialQuotationItems.length, purchaseMasterItems.length]);
 
  useEffect(() => {
    const fetchMaterialRequest = async () => {
      const leadId =
        selectedProject?.leadId ||
        selectedProject?.lead?._id ||
        selectedProject?.lead ||
        selectedProject?._id;

      if (isOpen && leadId) {
        setLoadingQuotation(true);
        try {
          const res = await api.get(`/verve-material-requests/lead/${leadId}`);

          let records = [];
          if (Array.isArray(res.data)) {
            records = res.data;
          } else if (Array.isArray(res.data?.data)) {
            records = res.data.data;
          } else if (res.data?.items) {
            records = [res.data];
          } else if (res.data?.data?.items) {
            records = [res.data.data];
          }

          let fetchedQuotationItems = [];
          records.forEach((record) => {
            if (Array.isArray(record.items)) {
              record.items.forEach((item) => {
                if (
                  item.category === "customItems" ||
                  item.sourceType === "customItems"
                ) {
                  return;
                }

                fetchedQuotationItems.push({
                  tempId: `${Date.now()}-${Math.random()}`,
                  name:
                    item.name ||
                    item.itemName ||
                    item.description ||
                    "Unnamed Material",
                  amount:
                    Number(
                      item.vervePrice ||
                      item.unitPrice ||
                      item.netAmount ||
                      0
                    ),
                  rawData: item,
                });
              });
            }
          });

          setInitialQuotationItems(fetchedQuotationItems);
          
          let estPrice = 0;
          if (Array.isArray(res.data)) {
             estPrice = res.data[0]?.estimatedPrice || 0;
          } else if (Array.isArray(res.data?.data)) {
             estPrice = res.data.data[0]?.estimatedPrice || 0;
          } else if (res.data?.estimatedPrice) {
             estPrice = res.data.estimatedPrice;
          } else if (res.data?.data?.estimatedPrice) {
             estPrice = res.data.data.estimatedPrice;
          }
          setInitialQuotationEstimatedPrice(estPrice);

          if (fetchedQuotationItems.length === 0) {
            setFetchError(
              "No materials found in Verve_Material_Requests for this lead."
            );
          } else {
            setFetchError(null);
          }

        } catch (err) {
          console.error("Failed to fetch material requests:", err);
          setFetchError(
            "Failed to fetch Material Requests: " +
              (err.response?.data?.message || err.message),
          );
          setInitialQuotationItems([]);
          setInitialQuotationEstimatedPrice(0);
        } finally {
          setLoadingQuotation(false);
        }
      } else if (isOpen) {
        setFetchError("Project is missing a Lead reference.");
        setInitialQuotationItems([]);
        setInitialQuotationEstimatedPrice(0);
      }

      // Fetch existing MaterialRequest documents for this project to load custom search items.
      // This is moved OUTSIDE the above try-catch so it always runs, even if initial quotation fetch fails or if there is no leadId!
      if (isOpen && selectedProject?._id) {
        try {
          const reqsRes = await api.get(`/verve-material-requests/lead/${selectedProject._id}`);
          const requestsList = reqsRes.data?.requests || reqsRes.data || [];
          const loadedHardware = [];

          requestsList.forEach((req) => {
            if (req.items && Array.isArray(req.items)) {
              req.items.forEach((it) => {
                const itemName = it.itemName || it.item?.itemName || "";
                const inv = it.inventoryId || {};
                const invId = inv._id || inv.id || it.itemId || inv;

                // Avoid duplicates in loadedHardware
                const alreadyAdded = loadedHardware.some(
                  (lh) => String(lh._id) === String(invId) || lh.name.toLowerCase().trim() === itemName.toLowerCase().trim()
                );
                if (!alreadyAdded) {
                  loadedHardware.push({
                    tempId: `${Date.now()}-${Math.random()}`,
                    name: itemName || "Unnamed Material",
                    amount: Number(inv.unitPrice || inv.purchaseRate || 0),
                    _id: String(invId),
                    rawData: {
                      ...it,
                      inventoryId: invId,
                      _id: invId,
                      quantity: it.quantity,
                      stock: inv.stock ?? inv.availableQty ?? 0,
                      availableQty: inv.stock ?? inv.availableQty ?? 0,
                      purchaseRate: inv.purchaseRate ?? inv.unitPrice ?? 0,
                      vervePrice: inv.vervePrice ?? inv.purchaseRate ?? inv.unitPrice ?? 0,
                      netAmount: (inv.vervePrice ?? inv.purchaseRate ?? inv.unitPrice ?? 0) * (it.quantity || 1),
                      articleId: inv.item?.itemCode || inv.item?.itemId || inv.articleId || "",
                      itemCode: inv.item?.itemCode || inv.item?.itemId || inv.itemCode || "",
                      category: inv.item?.category?.categoryName || inv.item?.category?.title || (typeof inv.item?.category === 'string' ? inv.item?.category : inv.category) || "",
                      subCategory: inv.item?.subCategory?.name || inv.subCategory || "",
                      vendorName: inv.vendor?.vendorName || inv.vendor?.name || inv.vendorName || "",
                      vendor: inv.vendor || null,
                      description: itemName,
                      itemName: itemName,
                      information: inv.item?.description || inv.information || "",
                      internalType: inv.item?.internalType || inv.internalType || "",
                      shutterType: inv.item?.shutterType || inv.shutterType || "",
                      width: inv.item?.width || inv.width || "",
                      height: inv.item?.height || inv.height || "",
                      depth: inv.item?.depth || inv.depth || "",
                      uom: inv.item?.uom || inv.item?.units?.name || inv.uom || "",
                      moq: inv.item?.moq || inv.moq || "",
                      purchasedQty: inv.purchasedQty || "",
                      taxableAmount: inv.taxableValue || inv.taxableAmount || "",
                      gstPercentage: inv.taxPercent || inv.gstPercentage || "",
                      gstAmount: inv.totalTax || inv.gstAmount || "",
                      invoiceNumber: inv.invoiceNo || inv.invoiceNumber || "",
                      remark: inv.item?.remarks || inv.remarks || inv.remark || ""
                    }
                  });
                }
              });
            }
          });

          setHardwareItems(loadedHardware);
        } catch (err) {
          console.error("Failed to fetch existing project requests:", err);
          setHardwareItems([]);
        }
      } else if (isOpen && !selectedProject?._id) {
        setHardwareItems([]);
      }
    };
    fetchMaterialRequest();
  }, [isOpen, selectedProject]);

  useEffect(() => {
    const fetchOrderingMaterials = async () => {
      const leadId =
        selectedProject?.leadId ||
        selectedProject?.lead?._id ||
        selectedProject?.lead ||
        selectedProject?._id;
      const projectId = selectedProject?._id;

      if (isOpen && (leadId || projectId)) {
        setLoadingOrdering(true);
        try {
          let quotes = [];
          if (leadId) {
            try {
              const res = await orderingQuotationService.getQuotations({ leadId });
              if (res && res.data) quotes = res.data;
              else if (Array.isArray(res)) quotes = res;
            } catch (e) {
              console.warn("Could not fetch ordering quotes by leadId:", e);
            }
          }
          if (quotes.length === 0 && projectId) {
            try {
              const res = await orderingQuotationService.getQuotations({ projectId });
              if (res && res.data) quotes = res.data;
              else if (Array.isArray(res)) quotes = res;
            } catch (e) {
              console.warn("Could not fetch ordering quotes by projectId:", e);
            }
          }

          let itemsList = [];
          if (Array.isArray(quotes)) {
            quotes.forEach((quote) => {
              if (Array.isArray(quote.rows)) {
                quote.rows.forEach((row, rIdx) => {
                  if (row.particulars || row.description || row.name || row.amount > 0) {
                    itemsList.push({
                      id: row.id || row._id || `${quote._id || Date.now()}-${rIdx}`,
                      name: row.particulars || row.name || row.description || "Unnamed Item",
                      description: row.description && row.description !== row.particulars ? row.description : "",
                      quantity: Number(row.quantity) || 1,
                      amount: Number(row.amount) || 0,
                    });
                  }
                });
              }
            });
          }
          setOrderingItems(itemsList);
        } catch (err) {
          console.error("Error fetching ordering materials:", err);
          setOrderingItems([]);
        } finally {
          setLoadingOrdering(false);
        }
      } else if (!isOpen) {
        setOrderingItems([]);
      }
    };

    fetchOrderingMaterials();
  }, [isOpen, selectedProject]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    setSelectedOfficerId("");
  }, [selectedDepartment]);

  const uniqueRoles = useMemo(() => {
    const rolesSet = new Set();
    allMembers.forEach(o => {
      const roleStr = o.installationTrade || o.role;
      if (roleStr && roleStr.toLowerCase() !== 'civil') rolesSet.add(roleStr.toLowerCase());
    });
    return Array.from(rolesSet).map(r => r.charAt(0).toUpperCase() + r.slice(1)).sort();
  }, [allMembers]);

  const departmentMembers = useMemo(() => {
    if (!selectedDepartment) return EMPTY_ARRAY;
    return allMembers.filter(
      (m) =>
        String(m.installationTrade || m.role || "").toLowerCase() ===
        selectedDepartment.toLowerCase(),
    );
  }, [allMembers, selectedDepartment]);

  useEffect(() => {
    if (departmentMembers.length > 0) {
      const firstId = String(departmentMembers[0]._id);
      if (selectedOfficerId !== firstId && !selectedOfficerId) {
        setSelectedOfficerId(firstId);
      }
    } else if (departmentMembers.length === 0 && selectedOfficerId !== "") {
      setSelectedOfficerId("");
    }
  }, [departmentMembers, selectedOfficerId]);

  // Initialize state when the modal opens with specific props
  useEffect(() => {
    if (isOpen) {
      setActiveTab("tab2");
      setSelectedItems(initialItems || EMPTY_OBJECT);
      setHasMappedInitial(false);
      setSelectedDepartment(initialDepartment || "");
      setSelectedProject(initialProject);
      setSelectedOfficerId("");
      setAssignmentError("");
      setSuccessMessage("");
      setFormData({ expectedDate: "", serviceType: "Verve", notes: "" });
      setSubmitting(false);
      setHardwareItems([]);
      setInitialQuotationItems([]);
      setInitialQuotationEstimatedPrice(0);
    } else {
      setHardwareItems([]);
      setInitialQuotationItems([]);
      setInitialQuotationEstimatedPrice(0);
    }
  }, [isOpen, initialProject, initialItems, initialDepartment]);

  const isManagement = useMemo(
    () =>
      ["owner", "admin", 'designer', 'store_incharge'].includes(
        viewerRole,
      ),
    [viewerRole],
  );

  const updateQty = (item, delta) => {
    const currentQty = selectedItems[item._id] || 0;
    const newQty = Math.max(0, currentQty + delta);

    if (newQty > item.stock) return;

    if (newQty === 0) {
      const { [item._id]: removed, ...rest } = selectedItems;
      setSelectedItems(rest);
    } else {
      setSelectedItems({ ...selectedItems, [item._id]: newQty });
    }
  };

  const setExactQty = (item, val) => {
    let newQty = parseInt(val, 10);
    if (isNaN(newQty) || newQty < 0) {
      const { [item._id]: removed, ...rest } = selectedItems;
      setSelectedItems(rest);
      return;
    }
    if (newQty > item.stock) newQty = item.stock;

    if (newQty === 0) {
      const { [item._id]: removed, ...rest } = selectedItems;
      setSelectedItems(rest);
    } else {
      setSelectedItems({ ...selectedItems, [item._id]: newQty });
    }
  };

  const estimatedValue = useMemo(() => {
    return selectedPurchaseItems.reduce((sum, item) => {
      const matchedEnriched = sortedFilteredPurchaseItems.find(s => String(s.item._id) === String(item._id));
      const isQuotation = matchedEnriched?.isRequestedInInitialQuotation;
      
      const qty = item.rawData?.quantity || selectedItems[item._id] || 0;
      const price = isQuotation && matchedEnriched.quotationPrice > 0 ? matchedEnriched.quotationPrice : Number(item.amount || 0);
      
      return sum + (price * qty);
    }, 0);
  }, [selectedPurchaseItems, selectedItems, sortedFilteredPurchaseItems]);

  const handleSubmit = async () => {
    const combinedItems = [...selectedPurchaseItems];
    console.log(combinedItems);
    console.log(combinedItems[0]);
    console.log(combinedItems[0]?.rawData);
    if (!selectedDepartment || selectedDepartment === "all") {
      setAssignmentError(
        "Please select a department.",
      );
      return;
    }

    if (!selectedProject?._id) {
      setAssignmentError("Please select a project first.");
      return;
    }

    if (combinedItems.length === 0) {
      setAssignmentError(
        "Please ensure there is at least one material to request.",
      );
      return;
    }

    setSubmitting(true);
    setAssignmentError("");
    setSuccessMessage("");
    try {
      const requestData = {
        projectId: selectedProject._id,
        items: combinedItems.map((item) => {
          const rawData = item.rawData || {};
          return {
            inventoryId: rawData.inventoryId || rawData._id || item._id || null,
            itemName: item.name || "Item",
            quantity: rawData.quantity || rawData.purchasedQty || 1,
          };
        }),
        expectedDate: formData.expectedDate,
        serviceType: formData.serviceType,
        notes: formData.notes,
        installationTrade: selectedDepartment,
        // Auto-assign first available member from department in background to satisfy backend
        assignedTo: departmentMembers[0]?._id || null,
        source,
      };

      console.log("Submitting request data payload:", requestData);
      console.log("Combined Items Source:", combinedItems);

      if (!requestId) {
        requestData.status = ['store_incharge'].includes(viewerRole) ? "AWAITING_OWNER_APPROVAL" : "PENDING";
      } else {
        if (['store_incharge'].includes(viewerRole)) {
          requestData.status = "AWAITING_OWNER_APPROVAL";
        }
      }

      if (requestId) {
        await materialRequestService.updateRequest(requestId, requestData);
      } else {
        await materialRequestService.createRequest(requestData);
      }

      if (onSuccess) onSuccess();

      setSuccessMessage(`Material requested successfully.`);

      // Auto-close modal after a short delay to show success message
      setTimeout(() => {
        setSuccessMessage("");
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Error creating request:", error);
      setAssignmentError(
        error?.response?.data?.message ||
          "Could not create request. Check team assignment and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white rounded-xl w-full max-w-[780px] h-[min(94svh,950px)] max-h-[94svh] border border-slate-200 flex flex-col overflow-hidden min-h-0 animate-in zoom-in-95 duration-300 shadow-xl shadow-slate-900/10 font-poppins">
        <MaterialRequestHeader
          project={selectedProject}
          projects={projects}
          onProjectChange={setSelectedProject}
          estimatedValue={estimatedValue}
          onClose={onClose}
        />

        {successMessage && (
          <div className="bg-emerald-50 border-b border-emerald-100 px-8 py-2.5 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
              <Plus className="w-3 h-3 text-white rotate-45" />
            </div>
            <span className="text-[11px] font-bold text-emerald-700 font-poppins">
              {successMessage}
            </span>
          </div>
        )}

        {assignmentError && (
          <div className="bg-rose-50 border-b border-rose-100 px-8 py-2.5 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
            <span className="text-[11px] font-bold text-rose-700 font-poppins">
              {assignmentError}
            </span>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200 px-8 bg-slate-50/50 shrink-0 gap-4">
          {/* <button
            type="button"
            onClick={() => setActiveTab('tab1')}
            className={`flex items-center gap-2 px-4 py-3 text-[12px] font-bold tracking-wider transition-all border-b-2 -mb-[1px] font-poppins cursor-pointer ${
              activeTab === 'tab1'
                ? 'border-[#2b448f] text-[#2b448f] bg-blue-50/20'
                : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50/80'
            }`}
          >
            <Package className="w-4 h-4" />
            Project Materials
          </button> */}
          <button
            type="button"
            onClick={() => setActiveTab('tab2')}
            className={`flex items-center gap-2 px-4 py-3 text-[12px] font-bold tracking-wider transition-all border-b-2 -mb-[1px] font-poppins cursor-pointer ${
              activeTab === 'tab2'
                ? 'border-[#2b448f] text-[#2b448f] bg-blue-50/20'
                : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50/80'
            }`}
          >
            <Plus className="w-4 h-4" />
            Request New Material
          </button>
        </div>

        {activeTab === 'tab1' ? (
          <div className="flex-1 overflow-y-auto p-6 bg-slate-50/40 flex flex-col gap-5 no-scrollbar min-h-[450px]">
            {/* Dropdown 1: Final Quotation Material */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden transition-all">
              <button
                type="button"
                onClick={() => setOpenFinalQuotation(!openFinalQuotation)}
                className="w-full px-6 py-4 flex items-center justify-between bg-slate-50/60 hover:bg-slate-100/60 transition-colors cursor-pointer border-b border-transparent data-[open=true]:border-slate-200"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 text-[#2b448f] flex items-center justify-center shrink-0 shadow-sm">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="text-[14px] font-bold text-slate-800 font-poppins">Final Quotation Material</span>
                    <span className="text-[11px] font-medium text-slate-400 font-poppins">Materials selected during Final Quotation stage</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="px-2.5 py-0.5 rounded-full bg-blue-50 text-[#2b448f] text-[11px] font-bold font-poppins">
                    {initialQuotationItems.length} {initialQuotationItems.length === 1 ? 'item' : 'items'}
                  </span>
                  {openFinalQuotation ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </button>

              {openFinalQuotation && (
                <div className="border-t border-slate-100 animate-in fade-in duration-200">
                  {loadingQuotation ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader2 className="w-6 h-6 text-[#2b448f] animate-spin" />
                    </div>
                  ) : initialQuotationItems.length === 0 ? (
                    <div className="p-10 text-center bg-slate-50/30 flex flex-col items-center justify-center gap-2">
                      <AlertCircle className="w-9 h-9 text-slate-300 mb-1" />
                      <span className="text-[13px] font-bold text-slate-600 font-poppins">Can't find any items or materials</span>
                      <span className="text-[11px] font-medium text-slate-400 font-poppins">No items selected in Final Quotation for this project.</span>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50/50 border-b border-slate-100">
                          <tr>
                            <th className="px-6 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider font-poppins">Material Name</th>
                            <th className="px-6 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider font-poppins text-center">Category</th>
                            <th className="px-6 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider font-poppins text-right">Price</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {initialQuotationItems.map((item, idx) => (
                            <tr key={item.tempId || idx} className="hover:bg-slate-50/50 transition-colors">
                              <td className="px-6 py-3.5">
                                <span className="text-[13px] font-semibold text-slate-800 font-poppins">{item.name}</span>
                              </td>
                              <td className="px-6 py-3.5 text-center">
                                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded bg-slate-100 text-[10px] font-bold text-slate-600 uppercase tracking-wider font-poppins">
                                  {item.rawData?.category || 'General'}
                                </span>
                              </td>
                              <td className="px-6 py-3.5 text-right">
                                <span className="text-[13px] font-bold text-slate-700 font-poppins">₹{item.amount}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Dropdown 2: Ordering Material / Items */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden transition-all">
              <button
                type="button"
                onClick={() => setOpenOrdering(!openOrdering)}
                className="w-full px-6 py-4 flex items-center justify-between bg-slate-50/60 hover:bg-slate-100/60 transition-colors cursor-pointer border-b border-transparent data-[open=true]:border-slate-200"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 shadow-sm">
                    <ShoppingCart className="w-5 h-5" />
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="text-[14px] font-bold text-slate-800 font-poppins">Ordering Material / Items</span>
                    <span className="text-[11px] font-medium text-slate-400 font-poppins">Materials and items selected during ordering stage</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold font-poppins">
                    {orderingItems.length} {orderingItems.length === 1 ? 'item' : 'items'}
                  </span>
                  {openOrdering ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </button>

              {openOrdering && (
                <div className="border-t border-slate-100 animate-in fade-in duration-200">
                  {loadingOrdering ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
                    </div>
                  ) : orderingItems.length === 0 ? (
                    <div className="p-10 text-center bg-slate-50/30 flex flex-col items-center justify-center gap-2">
                      <AlertCircle className="w-9 h-9 text-slate-300 mb-1" />
                      <span className="text-[13px] font-bold text-slate-600 font-poppins">Can't find any items or materials</span>
                      <span className="text-[11px] font-medium text-slate-400 font-poppins">No items selected in Ordering for this project.</span>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50/50 border-b border-slate-100">
                          <tr>
                            <th className="px-6 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider font-poppins">Item / Particulars</th>
                            <th className="px-6 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider font-poppins">Description</th>
                            <th className="px-6 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider font-poppins text-center">Qty</th>
                            <th className="px-6 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider font-poppins text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {orderingItems.map((item, idx) => (
                            <tr key={item.id || idx} className="hover:bg-slate-50/50 transition-colors">
                              <td className="px-6 py-3.5">
                                <span className="text-[13px] font-semibold text-slate-800 font-poppins">{item.name}</span>
                              </td>
                              <td className="px-6 py-3.5">
                                <span className="text-[12px] text-slate-500 font-poppins">{item.description || '-'}</span>
                              </td>
                              <td className="px-6 py-3.5 text-center">
                                <span className="text-[13px] font-bold text-slate-700 font-poppins">{item.quantity}</span>
                              </td>
                              <td className="px-6 py-3.5 text-right">
                                <span className="text-[13px] font-bold text-slate-700 font-poppins">₹{item.amount}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
        <>
          <div className="flex flex-1 min-h-0 flex-col overflow-hidden border-t border-slate-100">
          {/* Inventory Table Section - Full Width */}
          <section className="flex-1 min-h-0 min-w-0 flex flex-col bg-white">
            {/* Toolbar */}
            <div className="shrink-0 px-5 py-3 border-b border-slate-100 flex flex-col sm:flex-row items-center gap-4 bg-slate-50/20">
              <div className="relative shrink-0 w-full sm:w-auto">
                <select
                  value={selectedDepartment}
                  onChange={(e) => {
                    setSelectedDepartment(e.target.value);
                    setAssignmentError("");
                  }}
                  className="w-full sm:w-[170px] bg-white border border-slate-200 px-4 py-2 rounded-lg text-[12px] font-semibold text-slate-700 outline-none focus:border-blue-500 transition-all cursor-pointer appearance-none font-poppins"
                >
                  <option value="" disabled>
                    Select Department
                  </option>
                  {uniqueRoles.map((role) => (
                    <option key={role.toLowerCase()} value={role.toLowerCase()}>
                      {role}
                    </option>
                  ))}
                </select>
                <ChevronLeft className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 rotate-270 pointer-events-none" />
              </div>

              <div className="relative flex-1">
                 <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                 <input
                   type="text"
                   placeholder="Search purchase materials..."
                   value={searchTerm}
                   onChange={(e) => setSearchTerm(e.target.value)}
                   className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-[13px] font-medium text-slate-700 outline-none focus:border-blue-500 transition-all font-poppins placeholder:text-slate-400"
                 />
              </div>
            </div>

            {/* Table Content */}
            <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
              {loadingPurchases ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                </div>
              ) : filteredPurchaseItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                  <Package className="w-10 h-10 mb-3 opacity-20" />
                  <span className="text-[13px] font-bold font-poppins">No materials found</span>
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 bg-white z-10 border-b border-slate-100">
                    <tr>
                      <th className="px-5 py-4 text-[12px] font-semibold text-slate-700 font-poppins whitespace-nowrap">Material Detail</th>
                      {source !== "inventory" && sortedFilteredPurchaseItems.some(i => i.isRequestedInInitialQuotation) && (
                        <th className="px-5 py-4 text-[12px] font-semibold text-slate-700 font-poppins text-center whitespace-nowrap">Selected on Initial Quotation</th>
                      )}
                      <th className="px-5 py-4 text-[12px] font-semibold text-slate-700 font-poppins text-center whitespace-nowrap">Category</th>
                      <th className="px-5 py-4 text-[12px] font-semibold text-slate-700 font-poppins whitespace-nowrap">Vendor</th>
                      <th className="px-5 py-4 text-[12px] font-semibold text-slate-700 font-poppins text-center whitespace-nowrap">Queued</th>
                      <th className="px-5 py-4 text-[12px] font-semibold text-slate-700 font-poppins text-center whitespace-nowrap">In Stock</th>
                      <th className="px-5 py-4 text-[12px] font-semibold text-slate-700 font-poppins text-center whitespace-nowrap">Requested Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedFilteredPurchaseItems.map(({ item, isRequestedInInitialQuotation }) => {
                      const qty = selectedItems[item._id] || 0;
                      const isSelected = qty > 0;

                      return (
                        <tr 
                          key={item._id} 
                          className={`transition-all hover:bg-slate-50 ${isSelected ? "bg-blue-50/20" : "bg-white"}`}
                        >
                          <td className="px-5 py-4">
                            <div className="flex flex-col">
                              <span className="text-[13px] font-semibold text-slate-800 font-poppins">{item.name}</span>
                              <div className="flex items-center gap-1.5 mt-1">
                                <span className="text-[11px] text-slate-400 font-poppins">{item.itemCode}</span>
                                <span className="text-[11px] text-slate-300">•</span>
                                <span className="text-[11px] text-slate-500 font-poppins">₹{item.amount} / pc</span>
                              </div>
                            </div>
                          </td>
                          {source !== "inventory" && sortedFilteredPurchaseItems.some(i => i.isRequestedInInitialQuotation) && (
                            <td className="px-5 py-4 text-center">
                              {isRequestedInInitialQuotation ? (
                                <span className="inline-flex items-center justify-center px-2 py-1 rounded bg-blue-100 text-[10px] font-bold text-blue-600 font-poppins whitespace-nowrap">
                                  Selected on Initial Quotation
                                </span>
                              ) : (
                                <span className="text-[12px] text-slate-400 font-poppins">-</span>
                              )}
                            </td>
                          )}
                          <td className="px-5 py-4 text-center">
                            <span className="inline-flex items-center justify-center px-2 py-1 rounded bg-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-poppins">
                              {item.category || "General"}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide font-poppins">
                              {item.vendorName}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-center">
                            <span className="text-[13px] font-bold text-orange-500 font-poppins">
                              {item.queued}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-center">
                            <div className="flex items-baseline justify-center gap-1">
                              <span className="text-[13px] font-bold text-slate-700 font-poppins">{item.stock - qty}</span>
                              <span className="text-[10px] text-slate-400 font-poppins">Units</span>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex justify-center">
                              <div className="flex items-center gap-3 bg-white p-1 rounded-lg border border-slate-200 shadow-sm w-fit mx-auto">
                                <button
                                  onClick={() => updateQty(item, -1)}
                                  className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${
                                    qty > 0 ? "text-rose-500 hover:bg-rose-50" : "text-slate-300 cursor-not-allowed"
                                  }`}
                                  disabled={qty === 0}
                                >
                                  <Minus className="w-3.5 h-3.5" />
                                </button>
                                <input
                                  type="number"
                                  value={qty || ""}
                                  onChange={(e) => setExactQty(item, e.target.value)}
                                  placeholder="0"
                                  className="w-10 text-center text-[13px] font-bold text-slate-700 bg-transparent outline-none font-poppins placeholder:text-slate-300"
                                />
                                <button
                                  onClick={() => updateQty(item, 1)}
                                  className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${
                                    qty >= item.stock ? "text-slate-300 cursor-not-allowed" : "text-blue-500 hover:bg-blue-50"
                                  }`}
                                  disabled={qty >= item.stock}
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {/* {totalPages > 1 && (
              <div className="shrink-0 px-5 py-2 border-t border-slate-100 flex items-center justify-between bg-white">
                <span className="text-[11px] font-bold text-slate-400 font-poppins uppercase tracking-widest">
                  Page {currentPage} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <button 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                    className="w-7 h-7 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-30 transition-all cursor-pointer"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <button 
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    className="w-7 h-7 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-30 transition-all cursor-pointer"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )} */}
          </section>
        </div>

        <MaterialRequestFooter
          expectedDate={formData.expectedDate}
          onDateChange={(val) =>
            setFormData({ ...formData, expectedDate: val })
          }
          notes={formData.notes}
          onNotesChange={(val) => setFormData({ ...formData, notes: val })}
          onClose={onClose}
          onSubmit={handleSubmit}
          selectedCount={selectedPurchaseItems.length}
          submitting={submitting}
          assigneeSelected={Boolean(
            selectedDepartment && selectedDepartment !== "all",
          )}
          allowEmpty={true}
        />
        </>
        )}
      </div>
    </div>,
    document.body,
  );
};